import { CompletionVariant } from './types';

/**
 * Every user-facing string in the Speedy Flow, in one place.
 *
 * All of it is confirmed. Blocks that came from Figma name their frame and are verbatim — do not
 * reword those without a design change. The rest are screens that have no frame of their own; the
 * comment above each says what it is for, because the wording only makes sense against the moment
 * it appears in.
 *
 * Keeping them in one module is deliberate: a copy change should be a single reviewable diff, not a
 * hunt through seven components.
 */

/* -------------------------------------------------------------------------- */
/* Persona — Figma 8459-7191                                                  */
/* -------------------------------------------------------------------------- */

export const personaCopy = {
  title: '🌱 Join For Your Front Row Seat to the Wild',
  subtitle: 'What Brings You Here?',
  signUp: 'Sign up',
  logIn: 'I already have an account',
};

/* -------------------------------------------------------------------------- */
/* Sign up — Figma 8306-14368                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Only what the step itself puts on the page. The consent wording and its link to the terms live in
 * `SignupForm`, which owns the form this step renders — one copy of the promise, next to the
 * checkbox that is agreeing to it.
 */
export const signUpCopy = {
  title: "🦒 Watch & Connect with The World's Animals, 24/7",
  submit: 'Join',
};

/* -------------------------------------------------------------------------- */
/* Verify email — Figma 8306-14454 (error 8306-14846, resend 8306-15353)      */
/* -------------------------------------------------------------------------- */

/*
 * Deliberately absent. The verify step renders `routes/emailVerify/Verification`, which owns its
 * own wording, and the choose-product step renders the real product cards. Mirroring their strings
 * here would be worse than having none: the copy would look editable from this module and changing
 * it would do nothing on screen. To reword either, edit the component.
 */

/* -------------------------------------------------------------------------- */
/* Choose product — Figma 8306-15440                                          */
/* -------------------------------------------------------------------------- */

export const chooseProductCopy = {
  /** The only string this step owns; the cards below it bring their own. */
  logIn: 'I already have an account',
};

/* -------------------------------------------------------------------------- */
/* Await payment — Figma 8552-13492 covers the pre-redirect state only        */
/* -------------------------------------------------------------------------- */

export const awaitPaymentCopy = {
  redirecting: {
    title: 'Taking you to secure checkout...',
    body: 'Redirecting you to a new tab, do not close this tab.',
    footnote: 'Powered by Stripe',
  },
  // The guest path lands here after verifying their email, with a product already chosen. The button
  // exists so the checkout tab is opened by a real click — browsers block `window.open` outside a
  // user gesture — and so the hand-off is not a surprise.
  start: {
    title: 'Ready when you are',
    body: 'You picked {productName}. We will open secure checkout in a new tab.',
    cta: 'Continue to checkout',
  },
  // Shown while we wait for the webhook to grant the entitlement.
  waiting: {
    title: 'Confirming your payment...',
    body: 'Almost there — this usually takes a few seconds.',
  },
  // Must never read as a failure: the card was charged (ADR 0003).
  unconfirmed: {
    title: 'Payment received',
    body: "We're still unlocking your access. This can take a minute — you can keep browsing.",
    cta: 'Check again',
  },
  // The visitor arrives here with a product already chosen — resumed from a link, or picked before
  // they signed in — and cannot buy it: they already hold it, or the offer attached to it is spent.
  // The server's own reason is shown underneath, so this must not try to guess which one it is.
  refused: {
    title: 'That one is not available to you',
    body: 'Nothing has been charged. Have a look at what else is on offer.',
    cta: 'See the options',
  },
  // Reached when the visitor cancels or closes the checkout tab.
  cancelled: {
    title: 'No payment was taken',
    body: 'Nothing has been charged. Pick an option whenever you are ready.',
    cta: 'Back to options',
  },
};

/* -------------------------------------------------------------------------- */
/* Completion — Figma 8514-9818 covers the `purchase` variant only            */
/* -------------------------------------------------------------------------- */

export interface CompletionCopy {
  title: string;
  /**
   * Shown instead of `title` when the purchase was for one habitat and we can name the species.
   * Only the purchase variants have one: nothing was bought on the others, so there is no habitat
   * to name.
   */
  habitatTitle?: string;
  /** `{productName}` and `{species}` are replaced where the variant names them. */
  body: string;
  cta: string;
  /** Whether this variant also asks the persona question inline. */
  asksPersona: boolean;
}

export const completionCopy: Record<CompletionVariant, CompletionCopy> = {
  // From the design.
  purchase: {
    title: 'That worked!',
    habitatTitle: '🌱 Your Front Row Seat to {species} is Ready',
    body: 'You now have access to {productName}.',
    cta: 'Meet the {species}',
    asksPersona: false,
  },
  // The Sign-In Flow's confirmation — no product to name, so it cannot reuse the purchase card's
  // wording.
  account: {
    title: "You're in!",
    body: 'Your free account is ready. The animals are waiting.',
    cta: 'Start exploring',
    asksPersona: false,
  },
  // Same confirmation, plus the persona question. The heading and CTA are deliberately different
  // from the standalone Persona Step — this visitor has already joined, so "Sign up" would make no
  // sense as a button.
  'account-with-persona': {
    title: "You're in!",
    body: 'One last thing, so we can show you the right animals.',
    cta: 'Take me in',
    asksPersona: true,
  },
  // The purchase confirmation with the persona question folded in.
  'purchase-with-persona': {
    title: 'That worked!',
    habitatTitle: '🌱 Your Front Row Seat to {species} is Ready',
    body: 'You now have access to {productName}. One last thing, so we can point you at the right animals.',
    cta: 'Meet the {species}',
    asksPersona: true,
  },
  // For a partner-referred visitor who already holds what they came for — names it, and sells
  // nothing.
  'partner-welcome': {
    title: 'Welcome to Zoolife',
    body: 'Your access is already active. Nothing to buy — go and meet the animals.',
    cta: 'Start exploring',
    asksPersona: false,
  },
};

/**
 * Fill `{token}` placeholders in a copy string.
 *
 * A token with no value falls back to generic wording rather than being left in place. Leaving it
 * printed a literal "{productName}" to people who had just paid.
 */
const FALLBACKS: Record<string, string> = {
  productName: 'your new pass',
  // Bare, because every template that takes `{species}` already supplies its own article — "Meet
  // the {species}". A fallback carrying one of its own read "Meet the the animals" on the button a
  // paying customer presses, whenever the purchase was not scoped to a single habitat and there was
  // no species to name.
  species: 'animals',
};

export const interpolate = (template: string, values: Record<string, string | undefined>) =>
  template.replace(/\{(\w+)\}/g, (match, key) => values[key] ?? FALLBACKS[key] ?? match);

/**
 * The heading for a confirmation.
 *
 * A purchase scoped to a single habitat is confirmed by naming the animal rather than the
 * transaction — that is what the visitor actually came for. Both conditions are required: the
 * `species` fallback would otherwise promise a front row seat to "animals", which says less
 * than "That worked!", and a pass that covers every habitat must not be confirmed as if it were
 * about the one the flow happens to be standing in.
 */
export const completionTitle = (
  copy: CompletionCopy,
  { species, forOneHabitat }: { species?: string; forOneHabitat: boolean },
) => (forOneHabitat && species && copy.habitatTitle ? interpolate(copy.habitatTitle, { species }) : copy.title);

/** What each step is, for assistive technology. Not shown on screen. */
export const stepLabels: Record<string, string> = {
  persona: 'What brings you here',
  'sign-up': 'Create your account',
  'log-in': 'Log in',
  'verify-email': 'Verify your email',
  'choose-product': 'Choose your pass',
  'await-payment': 'Checkout',
  completion: 'All done',
};
